// src/components/issues/BadgeNcs.tsx — SCE Issues
// Le badge NC-S de l'en-tête de fiche, qui dit OÙ EN EST la NC-S chez la
// qualité (06/10, piste B de https://claude.ai/artifact/TjvdvyxmpAUpsPHFpcXPqw) :
// « NC-S_2026-97 ◷ ». Au repos, le symbole seul à côté du N° (pour ne pas
// surcharger l'en-tête) ; l'état se déplie au survol. Un clic ouvre le détail — composant,
// fournisseur, pilote, décision, avancement — et, tant que le N° manque, sa
// recherche dans le registre. Rien d'autre sur la fiche : pas de carte en
// plus, rien dans l'activité.

import { useState, type JSX } from 'react'
import { ExternalLink } from 'lucide-react'
import { Popover } from '@/components/ui/Popover'
import { IconeStatut } from '@/components/ui/EtatProbleme'
import { ReferenceNcs } from '@/components/issues/ReferenceNcs'
import { useSuiviNcs } from '@/hooks/useSuiviNcs'
import type { EtatReferenceNcs } from '@/hooks/useReferenceNcs'
import type { Candidat } from '@/utils/registreNcs'
import { declarationDepuisNom } from '@/utils/registreNcs'
import {
  LIBELLES_ETAT_NCS,
  avancementLisible,
  decisionLisible,
  etatNcs,
  type EtatNcs,
} from '@/utils/suiviNcs'
import type { Statut } from '@/types/issue.types'

/** Mêmes icônes que les listes : pointillés, horloge, coche verte, barré. */
const ICONE: Record<EtatNcs, Statut> = {
  attente: 'Ouvert',
  enregistree: 'En cours',
  ouverte: 'En cours',
  terminee: 'Clos',
  annulee: 'Annulé',
}

export interface DeclarationAffichee {
  fichier: string
  lien: string
  /** ISO */
  date: string
  auteur: string
}

function jourCourt(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
}

export interface BadgeNcsProps {
  /** N° de la NC-S (RefNCS), s'il est connu. */
  refNcs: string | undefined
  /** La dernière NC-S envoyée depuis l'appli (activité), s'il y en a une. */
  declaration: DeclarationAffichee | null
  /** Recherche du N° dans le registre, tant qu'il manque. */
  recherche: EtatReferenceNcs
  onChercher: () => void
  onChoisir: (c: Candidat) => void
}

export function BadgeNcs({ refNcs, declaration, recherche, onChercher, onChoisir }: BadgeNcsProps): JSX.Element {
  const suivi = useSuiviNcs(refNcs)
  const etat = etatNcs(refNcs, suivi)
  const [ancre, setAncre] = useState<HTMLButtonElement | null>(null)
  const [ouvert, setOuvert] = useState(false)

  // Le composant : celui du registre (retapé par la qualité), sinon celui du formulaire envoyé.
  const envoyee = declaration ? declarationDepuisNom(declaration.fichier, '', '', '') : null
  const composant = suivi
    ? [suivi.nvs.join(' + '), suivi.designation, suivi.quantite && `${suivi.quantite} pièce${suivi.quantite === '1' ? '' : 's'}`]
    : [envoyee?.nvs.join(' + '), envoyee?.designation]
  const ligneComposant = composant.filter(Boolean).join(' · ')
  const decision = suivi ? decisionLisible(suivi) : ''
  const avancement = suivi ? avancementLisible(suivi) : ''
  // Une ou plusieurs lignes du registre ressemblent à la NC-S, sans certitude :
  // le badge le signale, le détail demande de confirmer.
  const aConfirmer = !refNcs && recherche.etat === 'a-choisir'

  return (
    <>
      <button
        ref={setAncre}
        type="button"
        className="badge-ncs"
        aria-expanded={ouvert}
        aria-haspopup="dialog"
        onClick={() => setOuvert((o) => !o)}
        title={aConfirmer ? 'N° de NC-S trouvé dans le registre qualité : à confirmer' : 'Où en est la NC-S chez la qualité'}
      >
        <span className="badge-ncs-numero">{refNcs ?? 'NC-S'}</span>
        {aConfirmer ? (
          <span className="badge-ncs-etat badge-ncs-confirmer">
            <span className="badge-ncs-alerte" aria-hidden="true">!</span>
            <span className="badge-ncs-libelle">N° à confirmer</span>
          </span>
        ) : (
          <span className="badge-ncs-etat">
            <IconeStatut statut={ICONE[etat]} />
            <span className="badge-ncs-libelle">{LIBELLES_ETAT_NCS[etat]}</span>
          </span>
        )}
      </button>

      {ouvert && ancre && (
        <Popover ancre={ancre} onClose={() => setOuvert(false)} titre={refNcs ?? 'NC-S'} largeur={380}>
          <div className="ncs-detail">
            <div className="ncs-detail-etat">
              <IconeStatut statut={ICONE[etat]} />
              {etat === 'attente'
                ? 'En attente d’enregistrement par la qualité'
                : etat === 'enregistree'
                  ? 'Enregistrée par la qualité'
                  : `${LIBELLES_ETAT_NCS[etat]} chez la qualité`}
            </div>

            <dl className="ncs-detail-lignes">
              {ligneComposant && <><dt>Composant</dt><dd>{ligneComposant}</dd></>}
              {suivi?.fournisseur && <><dt>Fournisseur</dt><dd>{suivi.fournisseur}{suivi.pays ? ` (${suivi.pays})` : ''}</dd></>}
              {suivi?.pilote && <><dt>Pilote</dt><dd>{suivi.pilote}</dd></>}
              {decision && <><dt>Décision</dt><dd>{decision}</dd></>}
              {avancement && <><dt>Avancement</dt><dd>{avancement}</dd></>}
              {suivi?.clotureLe && <><dt>Clôturée le</dt><dd>{jourCourt(suivi.clotureLe)}</dd></>}
            </dl>

            {refNcs && !suivi && (
              <p className="ncs-detail-aide">
                Le suivi de la qualité s’affichera ici dès que quelqu’un qui a accès à son registre aura ouvert l’appli.
              </p>
            )}
            {!refNcs && <ReferenceNcs etat={recherche} onChercher={onChercher} onChoisir={onChoisir} />}

            {declaration && (
              <div className="ncs-detail-pied">
                <span>Envoyée le {jourCourt(declaration.date)}{declaration.auteur ? ` par ${declaration.auteur}` : ''}</span>
                {declaration.lien && (
                  <a href={declaration.lien} target="_blank" rel="noreferrer">
                    Formulaire <ExternalLink size={13} aria-hidden="true" />
                  </a>
                )}
              </div>
            )}
          </div>
        </Popover>
      )}
    </>
  )
}
